import axios from 'axios';
import { SearchFilterModel } from 'ts/hotel/interfaces/SearchFilter';
import {
  CancellationPolicyResponse,
  GetRoomJsonParams,
  Hotel,
  HotelMatchingResponse,
  HotelResponse,
  NeighborhoodHotel,
  PreferedResponse,
  TripAdvisorResponse,
} from 'ts/hotel/interfaces/HotelResponse';
import { CreateProposalResponse, DetailsPageParams } from 'ts/hotel/interfaces/Generic';
import { GeoPolygon, HotelDetailsResponse, OneHotelResponse } from 'ts/hotel/interfaces/HotelDetails';
import {
  BookCompleteResponse,
  BookResponse,
  CheckPaymentStatusResponse,
  Credit2000,
  Pax,
} from 'ts/hotel/interfaces/BookResponse';
import { getKey, matchesHotelUkey, pascalToCamel } from 'utils/hotel';
import { HotelInfoResponseData } from 'ts/hotel/interfaces/HotelInfoResponse';
import { SerpResponse } from 'ts/hotel/interfaces/SerpResponse';
import { decode, encode } from '@msgpack/msgpack';
import { FilterModel, getSupplierItemKey, SupplierItem } from 'ts/hotel/classes/FilterModel';

interface getHotelsParams {
  searchToken: string;
  pageSize: number;
  sysToken?: string;
}

interface BeginHotelSearchResponse {
  searchToken: string;
  geoPolygons?: GeoPolygon[][];
  sysToken?: string;
  geoSearch?: any;
  neighborhoodList?: NeighborhoodHotel[];
}

interface NeighborhoodResponse {
  result: {
    searchByECity: {
      geoPolygons: {
        listOfGeoPolygon: {
          latitude: number;
          longitude: number;
        }[];
      }[];
    };
  };
}

interface BookCompleteParams {
  selectedRevolutCurrency?: string;
  useLevel2Price?: boolean;
  orderId: number;
  companyId: string;
  tripId: string;
  couponAmount: number;
  paxes: Pax[];
  creditCard: boolean;
  paymentSettings?: Credit2000;
  multiPaymentSettings?: Credit2000[];
  screenshots: string[];
  specialRequestMessage: string;
  docket: string;
  tourSubject: string;
  confirmPriceChange?: boolean;
}

export interface CreateProposalParams {
  comment: string;
  hotels: {
    htuKey: string;
    rcUniqKey: string;
    price: number;
  }[];
  searchToken: string;
  showPrice: boolean;
}

interface CreateMapDirectionParams {
  searchToken: string;
  html: string;
}

export const getSearchFilter = async (): Promise<SearchFilterModel | undefined> => {
  const key = 'hotelsSearchFilters';
  const cached = sessionStorage.getItem(key);
  if (cached) {
    return JSON.parse(cached);
  } else {
    try {
      const url = 'Hotels/GetSearchFilters';
      const { data } = await axios.get(url);
      sessionStorage.setItem(key, JSON.stringify(data));
      return data;
    } catch (err) {
      return undefined;
    }
  }
};

export const beginHotelSearch = async (
  searchParams: any,
  controller: AbortController,
  hotelIndex?: number,
): Promise<BeginHotelSearchResponse | undefined> => {
  try {
    const url = 'Hotels/BeginHotelSearch';

    const data = (await axios.post(url, searchParams, { signal: controller.signal })).data;
    sessionStorage.setItem(getKey('searchTokenHotels', hotelIndex), data.searchToken);
    sessionStorage.setItem(
      getKey('displayCurrencyHotels', hotelIndex),
      data.currencyCode?.toLowerCase(),
    );

    let neighborhoodList = data.geoCityLocations
      ?.filter((location: any) => location.locationType?.code === 'neighborhood')
      .sort((a: any, b: any) => a.location?.desc.localeCompare(b.location?.desc))
      .map((location: any) => ({
        desc: location.location?.desc,
        code: location.location?.code,
      }));

    if (neighborhoodList.length) {
      const addList = [
        {
          desc: 'Unselect all',
          code: '-1',
        },
        {
          desc: 'Clear',
          code: '0',
        },
      ];

      neighborhoodList = [...addList, ...neighborhoodList];
    }

    return {
      searchToken: data.searchToken,
      geoPolygons: data.geoPolygons?.map((polygon: any) =>
        polygon.map(({ latitude, longitude }: any) => ({ lat: latitude, lng: longitude })),
      ),
      sysToken: data.sysToken,
      geoSearch: data.geoSearch,
      neighborhoodList: neighborhoodList,
    };
  } catch (err) {
    return undefined;
  }
};

const hotelsPollStatsKey = 'hotelsPollStats';

interface HotelsPollStats {
  searchToken: string;
  hotels: number;
  rooms: number;
  bySupplier: Record<string, number>;
  roomsBySupplier: Record<string, number>;
  minPriceBySupplier: Record<string, number>;
  codeBySupplier: Record<string, string>;
  responses: {
    hotels: number;
    rooms: number;
    bySupplier: Record<string, number>;
    roomsBySupplier: Record<string, number>;
  }[];
}

const getHotelSupplier = (hotel: Hotel) =>
  hotel.item?.sysSupp || hotel.item?.sysSuppCode || 'unknown';

const getHotelRoomsCount = (hotel: Hotel) => hotel.roomClasses?.length || 0;

const countHotelsBySupplier = (hotels: Hotel[]) =>
  hotels.reduce<Record<string, number>>((acc, hotel) => {
    const supplier = getHotelSupplier(hotel);
    acc[supplier] = (acc[supplier] || 0) + 1;
    return acc;
  }, {});

const countRoomsBySupplier = (hotels: Hotel[]) =>
  hotels.reduce<Record<string, number>>((acc, hotel) => {
    const supplier = getHotelSupplier(hotel);
    acc[supplier] = (acc[supplier] || 0) + getHotelRoomsCount(hotel);
    return acc;
  }, {});

const addCounts = (previous: Record<string, number>, added: Record<string, number>) => {
  const result = { ...previous };
  Object.entries(added).forEach(([key, count]) => {
    result[key] = (result[key] || 0) + count;
  });
  return result;
};

const addMinPrices = (previous: Record<string, number>, added: Record<string, number>) => {
  const result = { ...previous };
  Object.entries(added).forEach(([key, price]) => {
    if (!Number.isFinite(price)) {
      return;
    }
    result[key] = result[key] == null ? price : Math.min(result[key], price);
  });
  return result;
};

const addCodes = (previous: Record<string, string>, added: Record<string, string>) => {
  const result = { ...previous };
  Object.entries(added).forEach(([key, code]) => {
    if (code && !result[key]) {
      result[key] = code;
    }
  });
  return result;
};

const getHotelsPollStats = (searchToken: string): HotelsPollStats => {
  try {
    const cached = sessionStorage.getItem(hotelsPollStatsKey);
    const parsed = cached ? (JSON.parse(cached) as HotelsPollStats) : undefined;
    if (parsed?.searchToken === searchToken) {
      return {
        ...parsed,
        rooms: parsed.rooms || 0,
        roomsBySupplier: parsed.roomsBySupplier || {},
        minPriceBySupplier: parsed.minPriceBySupplier || {},
        codeBySupplier: parsed.codeBySupplier || {},
      };
    }
  } catch {}

  return {
    searchToken,
    hotels: 0,
    rooms: 0,
    bySupplier: {},
    roomsBySupplier: {},
    minPriceBySupplier: {},
    codeBySupplier: {},
    responses: [],
  };
};

const logHotelsAdded = (searchToken: string, result: HotelResponse) => {
  const hotels = result.hotels || [];
  const currency = sessionStorage.getItem('displayCurrencyHotels') || 'usd';
  const addedRooms = hotels.reduce((sum, hotel) => sum + getHotelRoomsCount(hotel), 0);
  const addedBySupplier = countHotelsBySupplier(hotels);
  const addedRoomsBySupplier = countRoomsBySupplier(hotels);
  const addedMinPriceBySupplier: Record<string, number> = {};
  const addedCodeBySupplier: Record<string, string> = {};

  hotels.forEach((hotel) => {
    const supplier = getHotelSupplier(hotel);
    const code = hotel.item?.sysSuppCode || '';
    if (code && !addedCodeBySupplier[supplier]) {
      addedCodeBySupplier[supplier] = code;
    }
    hotel.roomClasses?.forEach((room) => {
      const price = room.price?.[currency as keyof typeof room.price];
      if (typeof price === 'number' && Number.isFinite(price)) {
        addedMinPriceBySupplier[supplier] =
          addedMinPriceBySupplier[supplier] == null
            ? price
            : Math.min(addedMinPriceBySupplier[supplier], price);
      }
    });
  });

  const previous = getHotelsPollStats(searchToken);
  const bySupplier = addCounts(previous.bySupplier, addedBySupplier);
  const roomsBySupplier = addCounts(previous.roomsBySupplier, addedRoomsBySupplier);
  const minPriceBySupplier = addMinPrices(previous.minPriceBySupplier, addedMinPriceBySupplier);
  const codeBySupplier = addCodes(previous.codeBySupplier, addedCodeBySupplier);

  const stats: HotelsPollStats = {
    searchToken,
    hotels: previous.hotels + hotels.length,
    rooms: previous.rooms + addedRooms,
    bySupplier,
    roomsBySupplier,
    minPriceBySupplier,
    codeBySupplier,
    responses: [
      ...previous.responses,
      {
        hotels: hotels.length,
        rooms: addedRooms,
        bySupplier: addedBySupplier,
        roomsBySupplier: addedRoomsBySupplier,
      },
    ],
  };

  sessionStorage.setItem(hotelsPollStatsKey, JSON.stringify(stats));

  const formatLine = (
    name: string,
    added: number,
    total: number,
    addedRoomsCount: number,
    totalRooms: number,
  ) =>
    `${name}: Just Added - ${added}; Total - ${total}; Just Added rooms - ${addedRoomsCount}; Total rooms - ${totalRooms}`;

  const lines = [
    formatLine('hotels', hotels.length, stats.hotels, addedRooms, stats.rooms),
    ...Object.keys(stats.bySupplier).map((supplier) =>
      formatLine(
        supplier,
        addedBySupplier[supplier] || 0,
        stats.bySupplier[supplier],
        addedRoomsBySupplier[supplier] || 0,
        stats.roomsBySupplier[supplier] || 0,
      ),
    ),
  ];
  console.log(lines.join('\n'));
};

export const getSupplierItemsFromPollStats = (searchToken: string): SupplierItem[] => {
  const stats = getHotelsPollStats(searchToken);
  return Object.keys(stats.bySupplier)
    .sort((a, b) => a.localeCompare(b))
    .map((name) => ({
      suppDesc: name,
      suppCode: stats.codeBySupplier[name] || '',
      checked: false,
      hotels: stats.bySupplier[name],
      rooms: stats.roomsBySupplier[name] || 0,
      price: Number.isFinite(stats.minPriceBySupplier[name]) ? stats.minPriceBySupplier[name] : 0,
    }));
};

export const applyPollSupplierCounts = (filter: FilterModel): FilterModel => {
  const searchToken = sessionStorage.getItem('searchTokenHotels');
  if (!searchToken) {
    return filter;
  }

  const pollSuppliers = getSupplierItemsFromPollStats(searchToken);
  if (!pollSuppliers.length) {
    return filter;
  }

  const checkedKeys = new Set(
    (filter.suppliers || [])
      .filter((supplier) => supplier.checked)
      .map((supplier) => getSupplierItemKey(supplier)),
  );

  return {
    ...filter,
    suppliers: pollSuppliers.map((supplier) => ({
      ...supplier,
      checked: checkedKeys.has(getSupplierItemKey(supplier)),
    })),
  };
};

export const getHotels = async (
  params: getHotelsParams,
  controller: AbortController,
  isMsgpack: boolean = false,
): Promise<HotelResponse | undefined> => {
  try {
    const url = 'Hotels/GetHotels';

    if (isMsgpack) {
      const encodedData = encode({
        SearchToken: params.searchToken,
        PageSize: params.pageSize,
        SysToken: params.sysToken,
      });

      const response = await axios.post(url, encodedData, {
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/msgpack',
          Accept: 'application/msgpack',
        },
        responseType: 'arraybuffer',
      });

      const decodedData = decode(new Uint8Array(response.data));

      const result = pascalToCamel(decodedData);
      logHotelsAdded(params.searchToken, result);

      return result as HotelResponse;
    }
    const result = (await axios.post(url, params, { signal: controller.signal }))
      .data as HotelResponse;
    logHotelsAdded(params.searchToken, result);
    return result;
  } catch (err) {
    return undefined;
  }
};

export const stopPolling = (sysToken: string) => {
  try {
    const url = 'Hotels/StopPooling';
    axios.post(url, { sysToken });
  } catch (err) {
    return undefined;
  }
};

export const getClaudeInfo = async (searchToken: string): Promise<any | undefined> => {
  try {
    const url = 'Hotels/ClaudeHotelInfo';
    return (await axios.post(url, { searchToken })).data;
  } catch (err) {
    return undefined;
  }
};

export const getHotel = async (
  params: DetailsPageParams,
): Promise<HotelDetailsResponse | undefined> => {
  try {
    const url = 'Hotels/Details';

    const requestParams = {
      hotelUkey: params.id,
      searchToken: params.session,
      roomId: params.room,
      multiRoomsIds: params.rooms,
    };

    return (await axios.post(url, {}, { params: requestParams })).data;
  } catch (err) {
    return undefined;
  }
};

export const getSerpMatchingData = async (
  searchToken: string,
  controller: AbortController,
): Promise<any | undefined> => {
  try {
    const url = 'Hotels/GetSerpMatchingData';

    return (await axios.post(url, { searchToken }, { signal: controller.signal })).data;
  } catch (err) {
    return undefined;
  }
};

export const getHotelBook = async (
  params: DetailsPageParams,
): Promise<BookResponse | undefined> => {
  try {
    const url = 'Hotels/Book';

    const requestParams = {
      hotelUkey: params.id,
      searchToken: params.session,
      roomId: params.room,
      afterOneHotelSearch: true,
      language: localStorage.getItem('language') || 'he',
    };

    return (await axios.post(url, {}, { params: requestParams })).data;
  } catch (err) {
    return undefined;
  }
};

export const bookComplete = async (
  body: BookCompleteParams,
): Promise<BookCompleteResponse | undefined> => {
  try {
    const url = 'Hotels/BookComplete';
    const language = 1;

    return (await axios.post(url, { ...body, language })).data;
  } catch (err) {
    return undefined;
  }
};

export const createProposal = async (
  params: CreateProposalParams,
): Promise<CreateProposalResponse | undefined> => {
  try {
    const url = 'Hotels/CreateProposal';

    return (await axios.post(url, params)).data;
  } catch (err) {
    return undefined;
  }
};

export const createMapDirection = async (params: CreateMapDirectionParams): Promise<any> => {
  try {
    const url = '/Hotels/CreateMapDirectionsProposal';

    const formData = new FormData();
    formData.append('searchToken', params.searchToken);
    formData.append('html', params.html);

    return (await axios.post(url, formData)).data;
  } catch (err) {
    return undefined;
  }
};

export const getHotelsJson = async (
  params: GetRoomJsonParams,
): Promise<HotelMatchingResponse | undefined> => {
  try {
    const url = `/Hotels/Matching`;

    return (await axios.post(url, params)).data;
  } catch (err) {
    return undefined;
  }
};

export const checkPaymentStatus = async (
  transactionId: string,
): Promise<CheckPaymentStatusResponse | undefined> => {
  try {
    const url = 'Hotels/CheckPaymentStatus';

    return (await axios.post(url, { transactionId })).data;
  } catch (err) {
    return undefined;
  }
};

interface SubmitScreenshotsParams {
  screenshots: string[];
  orderId: number;
  lastScreenshot: string;
  transactionId?: string;
}
export const submitScreenshots = async (params: SubmitScreenshotsParams): Promise<any> => {
  try {
    const url = 'Hotels/SubmitScreenshots';

    return (await axios.post(url, params)).data;
  } catch (err) {
    return undefined;
  }
};

interface GetHotelInfoParams {
  searchToken: string;
  hotelUkey: string;
  roomBToken: string;
  afterOneHotelSearch?: boolean;
  aiStoreKey?: string;
}
export const getHotelInfo = async (
  params: GetHotelInfoParams,
  hotelIndex?: number,
): Promise<HotelInfoResponseData | undefined> => {
  try {
    const url = 'Hotels/HotelInfo';
    const hotelFromSearch = JSON.parse(
      sessionStorage.getItem(getKey('hotelFromSearch', hotelIndex)) || '{}'
    );
    const response = (await axios.post(url, null, { params })).data;

    return {
      ...response,
      hotelDetails: {
        ...response.hotelDetails,
        ...(matchesHotelUkey(hotelFromSearch, params.hotelUkey)
          ? { hotel: hotelFromSearch }
          : {}),
      },
    };
  } catch (err) {
    return undefined;
  }
};

export const getOneHotelSearchData = async (
  searchToken: string,
  controller: AbortController,
): Promise<OneHotelResponse | undefined> => {
  try {
    const url = 'Hotels/GetOneHotelSearchData';

    return (await axios.post(url, { searchToken }, { signal: controller.signal })).data;
  } catch (err) {
    return undefined;
  }
};

interface GetBeginOneHotelSearchParams {
  searchToken: string;
  hotels: {
    roomBToken: string;
    multiRoomsBTokens: string[];
    hotelItemCode: string;
  }[];
}
export const beginOneHotelSearch = async (
  params: GetBeginOneHotelSearchParams,
): Promise<{ searchToken: string; hasErrors: boolean; sessionExpired: boolean } | undefined> => {
  try {
    const url = 'Hotels/BeginOneHotelSearch';

    return (await axios.post(url, params)).data;
  } catch (err) {
    return undefined;
  }
};

export const chargeConditions = async (params: {
  searchToken: string;
  roomBTokenList: string[];
}): Promise<CancellationPolicyResponse | undefined> => {
  const url = '/Hotels/ChargeConditions';

  try {
    return (await axios.post(url, { ...params, language: 'He' })).data;
  } catch (err) {
    return undefined;
  }
};

export const getMonitorLink = (hotel: Hotel, baseUrl: string): string => {
  const params = {
    hotelID: hotel.itemCode,
    cityCode: hotel.info.cityName,
    suppCode: hotel.item?.sysSuppCode || '',
    bpid: hotel.item?.bpid || '',
    name: hotel.item?.name || '',
    stars: hotel.item?.star + '',
  };

  return baseUrl + '/Hotels/Index?' + new URLSearchParams(params).toString();
};

export const sendEmailProposal = async (email: string, url: string): Promise<any> => {
  try {
    return (await axios.post('/Hotels/SendProposalPdfByEmail', { email, url })).data;
  } catch (err) {
    return undefined;
  }
};

interface SavePreferredHotelParams {
  hotelItemBPID: string;
  hotelItemName: string;
  hotelAddressInfoAddress: string;
  hotelAddressInfoPhone: string;
  hotelAddressInfoCountry: string;
  hotelItemSysSuppCode: string;
  hotelItemCode: string;
  searchToken: string;
  recommended: boolean;
  recommendedPositive: string;
  recommendedNegative: string;
  languageCode: string;
  negativeTags: string[];
  positiveTags: string[];
}

export const savePreferredHotel = async (params: SavePreferredHotelParams): Promise<any> => {
  try {
    return (await axios.post('/Hotels/SavePreferredHotel', params)).data;
  } catch (err) {
    return undefined;
  }
};

export const getPreferredHotel = async (params: {
  hotelItemBPID: string;
  searchToken: string;
}): Promise<PreferedResponse | undefined> => {
  try {
    return (await axios.post('/Hotels/GetPreferredHotel', params)).data;
  } catch (err) {
    return undefined;
  }
};

interface UpdatePaymentBlockParams {
  sourcePrice: number;
  packageCurrency: string;
  addPrice: number;
  searchToken: string;
  paymentCount?: number;
  lang: string;
}

export const updatePaymentBlock = async (params: UpdatePaymentBlockParams) => {
  try {
    const url = 'Utils/UpdatePaymentBlock';
    return (await axios.post(url, undefined, { params })).data;
  } catch (err) {
    return undefined;
  }
};

interface GetNeighborhoodParams {
  code: string;
  searchToken: string;
}
export const getNeighborhood = async (params: GetNeighborhoodParams) => {
  try {
    return (await axios.post<NeighborhoodResponse>('/Hotels/GetNeighborhood', params)).data;
  } catch (err) {
    return undefined;
  }
};

interface HotelsBeproAIParams {
  bpid: string;
  hotelName: string;
  hotelAddress: string;
  cityName: string;
  countryCode: string;
  lang: string;
}
export const hotelsBeproAI = async (params: HotelsBeproAIParams) => {
  try {
    return (await axios.post('/Hotels/BeproAI', params)).data;
  } catch (err) {
    return undefined;
  }
};

interface SearchSerpHotelsParams {
  recaptchaToken: string;
  aiStoreKey?: string;
  cleanHotelName: string;
  countryCode: string;
  bpid: string;
  lat: number;
  lon: number;
  cleanAddress: string;
  searchToken: string;
  query: {
    q: string;
    gL: string;
    hl: string;
    checkIn: string;
    checkOut: string;
    adults: number;
    children: number;
    childrenAges?: number[];
    currency: string;
  };
}
export const searchSerpHotels = async (
  params: SearchSerpHotelsParams,
): Promise<SerpResponse | undefined> => {
  try {
    return (await axios.post('/Hotels/SearchSerpHotels', params)).data;
  } catch (err) {
    return undefined;
  }
};

export const getHotelInfoRaw = async (params: GetHotelInfoParams): Promise<any | undefined> => {
  try {
    const url = 'Hotels/HotelInfoRaw';
    return (await axios.post(url, null, { params })).data;
  } catch (err) {
    return undefined;
  }
};

interface HotelsClaudeParams {
  aiStoreKey: string;
}
export const hotelsClaude = async (params: HotelsClaudeParams): Promise<any | undefined> => {
  try {
    return (await axios.post('/Hotels/ClaudeProposalHotelInfo', params)).data;
  } catch (err) {
    return undefined;
  }
};

interface HotelsTripadvisorParams {
  bpid: string;
  lang: string;
  aiStoreKey?: string;
}
export const hotelsTripadvisor = async (
  params: HotelsTripadvisorParams,
): Promise<TripAdvisorResponse | undefined> => {
  try {
    return (await axios.post('/Hotels/TripAdvisor', params)).data;
  } catch (err) {
    return undefined;
  }
};

export const saveUserConfig = async (config: any): Promise<any | undefined> => {
  try {
    const url = 'Account/SaveUserConfig';

    const configStr = {
      ...config,
      input: {
        base: JSON.stringify(config.input.base),
        hotel: JSON.stringify(config.input.hotel),
      },
    };

    const { data } = await axios.post(url, configStr);
    if (data?.success) {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userLocal = JSON.parse(userStr);
        userLocal.config = config.input;
        localStorage.setItem('user', JSON.stringify(userLocal));
      }
    }

    return data;
  } catch (err) {
    return undefined;
  }
};

interface GetGoogleAutocompleteParams {
  latitude: number;
  longitude: number;
  radius: number;
  query: string;
}
export const getGoogleAutocomplete = async (
  params: GetGoogleAutocompleteParams,
  signal: any,
): Promise<any[]> => {
  try {
    return JSON.parse(
      (await axios.post('/Utils/GetGoogleAutocomplete', params, { signal })).data.googleResponse,
    ).results;
  } catch (err) {
    return [];
  }
};

export const addLogs = async (message: string) => {
  try {
    const url = 'Utils/Log';
    return (await axios.post(url, { message })).data;
  } catch (err) {
    return undefined;
  }
};
export const loadGoogleByProposal = async (
  htl: any,
  lang: string,
  token: string,
  searchToken: string,
  aiStoreKey?: string,
) => {
  const childrenAges = (htl.childrenAges || []).filter(
    (age: number) => Number.isFinite(age) && age >= 0,
  );
  const childrenOccupancy =
    childrenAges.length > 0
      ? { children: childrenAges.length, childrenAges }
      : { children: htl.children || 0 };
  const response = await searchSerpHotels({
    searchToken: searchToken,
    aiStoreKey: aiStoreKey,
    recaptchaToken: token,
    cleanHotelName: htl.item.item.name,
    bpid: htl.item.item.bpid,
    countryCode: htl.item.addressInfo.country,
    lat: htl.item.position.latitude,
    lon: htl.item.position.longitude,
    cleanAddress: htl.item.addressInfo.address,
    query: {
      q: `Hotel: ${htl.item.item.name}. Address: ${htl.item.addressInfo.address}, ${htl.item.addressInfo.city}, ${htl.item.addressInfo.country}`,
      gL: 'il',
      hl: lang,
      checkIn: htl.checkIn || '',
      checkOut: htl.checkOut || '',
      adults: htl.adults || 0,
      ...childrenOccupancy,
      currency: htl.currency?.toUpperCase() || 'USD',
    },
  });

  return response;
};
interface GetGooglePredictionParams {
  query: string;
  prefix: string;
}
export const getGooglePrediction = async (
  params: GetGooglePredictionParams,
  signal: any,
): Promise<any[]> => {
  try {
    const url = '/Utils/GetGooglePrediction';

    const res = await axios.post(url, params, { signal });

    const json = JSON.parse(res.data.googleResponse);
    return json.predictions;
  } catch (err) {
    return [];
  }
};

interface GetGooglePredictionParams {
  query: string;
  prefix: string;
}
export const getGooglePredictionDetails = async (placeId: string): Promise<any[]> => {
  try {
    const url = '/Utils/GetGooglePredictionDetails';
    const res = await axios.post(url, { placeId });
    const json = JSON.parse(res.data.googleResponse);
    return json;
  } catch (err) {
    return [];
  }
};

export interface CurrencyBanner {
  sysCurrencyCode: string;
  convert_eur_rate: number;
  convert_gbp_rate: number;
  convert_loc_rate: number;
  convert_usd_rate: number;
  lastUpdateDate: string;
}

export const getCurrencyBanner = async (): Promise<CurrencyBanner[] | undefined> => {
  try {
    const url = 'Account/GetCurrencyBanner';
    return (await axios.get(url)).data.currencies;
  } catch (err) {
    return undefined;
  }
};

export const getHotelDestination = async (bpid: string): Promise<any | undefined> => {
  try {
    const url = 'Utils/HotelDestination';
    return (await axios.post(url, { bpid })).data;
  } catch (err) {
    return undefined;
  }
};

export interface FacilityTreeItem {
  facility_id: number;
  facility_code: string;
  name: string;
  hotlist?: number;
  display?: number;
  display_order?: number;
}

export interface FacilityTreeSubgroup {
  subgroup_code: number;
  name: string;
  display_mode: string;
  facilities: FacilityTreeItem[];
}

export interface FacilityTreeGroup {
  group_code: number;
  name: string;
  subgroups: FacilityTreeSubgroup[];
}

export interface FacilityTreeResponse {
  Tree: FacilityTreeGroup[];
}

const getSharedHotelFacilities = (): FacilityTreeResponse => {
  try {
    const initSiteData = JSON.parse(localStorage.getItem('initSiteData') || '{}');
    const raw = initSiteData.sharedHotelFacilities;
    if (!raw) return { Tree: [] };
    return (typeof raw === 'string' ? JSON.parse(raw) : raw) as FacilityTreeResponse;
  } catch {
    return { Tree: [] };
  }
};

export const getFacilityTree = (): FacilityTreeResponse => getSharedHotelFacilities();

export const getHotlistFacilities = (): FacilityTreeItem[] =>
  (getFacilityTree().Tree || [])
    .flatMap((group) => (group.subgroups || []).flatMap((subgroup) => subgroup.facilities || []))
    .filter((facility) => facility.hotlist === 1)
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
